import { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import { showSbWarning } from "Utils/sbModal";
import useAuthStore from "Store/useAuthStore";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import { EmployeeSkillsContext } from "State/EmployeeSkillsContext";
import {
  getSBEmployeeJdSkillAnalysis,
  getSBEmployeeAdditionalSkills,
  submitProficiencyChangeReasons,
  getSBEmployeeScaleDefinition,
} from "Api/apiService";
import {
  VIEW_MODE_JD,
  DEFAULT_PAGE_SIZE,
  SUCCESS_TIMEOUT_MS,
  MIN_PROFICIENCY,
  MAX_PROFICIENCY,
  TEXT,
  TOUR_STEPS,
  TOUR_MODULE_KEY,
  sortSkillsByImportance,
} from "./EmployeeSkillsBridgerV2.constants";

const useEmployeeSkillsBridger = () => {
  const email = useAuthStore((s) => s.email);
  const accessToken = useAuthStore((s) => s.accessToken);
  const { isDrawerOpen, toggleDrawer } = useContext(EmployeeSkillsContext);
  const drawerTypeRef = useRef(null);

  // ── Data state ────────────────────────────────────────────────────────────

  const [loading, setLoading] = useState(false);
  const [allSkills, setAllSkills] = useState([]);
  const [sbiscore, setSbiscore] = useState(null);
  const [scaleData, setScaleData] = useState(null);
  const [jdMarkdown, setJdMarkdown] = useState("");
  // The API has no explicit "jd_assigned" flag: an employee with no JD assigned
  // resolves as either a 404 or a 200 with no skills/JD text. `assigned` is reset
  // to false before every JD-mode fetch so the button never shows mid-request or
  // after an error. `visible` is the user's own open/close toggle for the panel.
  const [jdPanel, setJdPanel] = useState({ assigned: false, visible: true });

  // ── UI state ──────────────────────────────────────────────────────────────

  const [viewMode, setViewMode] = useState(VIEW_MODE_JD);
  // Shared tour wiring: controlled state + pause/resume, the per-user "seen" flag,

  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin: startTour,
    goToStep: goToTourStep,
    close: closeMainTour,
    pause: pauseMainTour,
    resume: resumeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY });
  // Second, in-drawer tour (Save and Infer → Agree & Submit). The main tour hands
  // off to it on the Source Files step, then resumes when it completes.
  const [drawerTourActive, setDrawerTourActive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Guided-tour target refs (attached to real elements in the page) ─────────
  const tourTargetRefs = {
    sourceFiles: useRef(null),
    gauge: useRef(null),
    table: useRef(null),
    selfReported: useRef(null),
    scale: useRef(null),
    toggle: useRef(null),
  };
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [viewSuccess, setViewSuccess] = useState(false);
  const [viewToaster, setViewToaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Per-row proficiency state (keyed by skill_id) ─────────────────────────

  const [selfReportedProficiencies, setSelfReportedProficiency] = useState({});
  const [invalidInput, setInvalidInput] = useState({});
  const [showReasonInput, setShowReasonInput] = useState({});
  const [reasonText, setReasonText] = useState({});

  // ── API calls ─────────────────────────────────────────────────────────────

  const fetchScaleDefinition = useCallback(async () => {
    try {
      const response = await getSBEmployeeScaleDefinition();
      if (response) setScaleData(response);
    } catch (err) {
      console.error("Error fetching scale definition:", err);
    }
  }, []);

  const fetchSkillDetails = useCallback(async () => {
    setLoading(true);
    setCurrentPage(1);
    setAllSkills([]);
    if (viewMode === VIEW_MODE_JD) setJdPanel((prev) => ({ ...prev, assigned: false }));
    try {
      const response =
        viewMode === VIEW_MODE_JD
          ? await getSBEmployeeJdSkillAnalysis()
          : await getSBEmployeeAdditionalSkills();

      if (response) {
        const profileAnalysis =
          viewMode === VIEW_MODE_JD
            ? response.personProfileAnalysis
            : response.additional_skills?.personProfileAnalysis;
        const skills = profileAnalysis?.skills ?? [];
        const tasks = profileAnalysis?.tasks ?? [];
        setAllSkills(sortSkillsByImportance(skills, tasks));
        if (viewMode === VIEW_MODE_JD) {
          const jdText = response.job_description_md ?? "";
          setSbiscore(response?.current_vs_required_SBI_score ?? 0);
          setJdMarkdown(jdText);
          setJdPanel((prev) => ({ ...prev, assigned: skills.length > 0 || Boolean(jdText) }));
        } else if (skills.length === 0) {
          setSbiscore(0);
        }
      }
    } catch (err) {
      console.error("Error fetching skills data:", err);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => { fetchScaleDefinition(); }, [fetchScaleDefinition]);

  useEffect(() => {
    if (!isDrawerOpen) fetchSkillDetails();
  }, [isDrawerOpen, fetchSkillDetails]);

  // Seed self-reported map from API response
  useEffect(() => {
    const initial = {};
    allSkills.forEach((skill) => {
      initial[skill.skill_id] = skill.self_reported_proficiency ?? null;
    });
    setSelfReportedProficiency(initial);
  }, [allSkills]);

  // Reset to page 1 only when page size changes; data-triggered resets happen
  // explicitly in fetchSkillDetails to avoid the race where a slow API response
  // fires this effect AFTER the user has already navigated to a non-first page.
  useEffect(() => { setCurrentPage(1); }, [pageSize]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const paginatedSkills = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allSkills.slice(start, start + pageSize);
  }, [allSkills, currentPage, pageSize]);

  // Skill currently awaiting a reason
  const activeReasonSkillId = useMemo(
    () => Object.keys(showReasonInput).find((id) => showReasonInput[id]) ?? null,
    [showReasonInput]
  );

  const activeReasonSkill = useMemo(
    () => (activeReasonSkillId ? allSkills.find((s) => String(s.skill_id) === String(activeReasonSkillId)) : null),
    [activeReasonSkillId, allSkills]
  );

  // Panel renders only once a JD is confirmed assigned AND the user hasn't closed it.
  const showJdPanel = jdPanel.assigned && jdPanel.visible;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelfReportedChange = useCallback((key, value, currentProficiency) => {
    const isValid =
      value === null ||
      (typeof value === "number" &&
        Number.isInteger(value) &&
        value >= MIN_PROFICIENCY &&
        value <= MAX_PROFICIENCY);

    if (!isValid && value !== null) {
      setInvalidInput((p) => ({ ...p, [key]: true }));
      setSelfReportedProficiency((p) => ({ ...p, [key]: value }));
      setShowReasonInput((p) => ({ ...p, [key]: false }));
      return;
    }

    setInvalidInput((p) => ({ ...p, [key]: false }));
    setSelfReportedProficiency((p) => ({ ...p, [key]: value }));

    if (value !== null && Number(value) !== Number(currentProficiency)) {
      setShowReasonInput((p) => ({ ...p, [key]: true }));
    }

    if (Number(value) === Number(currentProficiency)) {
      showSbWarning({
        title: TEXT.EQUAL_PROFICIENCY_MODAL_TITLE,
        content: TEXT.EQUAL_PROFICIENCY_MODAL_CONTENT,
        okText: TEXT.EQUAL_PROFICIENCY_MODAL_OK,
      });
    }
  }, []);

  const handleReasonTextChange = useCallback((skillId, value) => {
    setReasonText((p) => ({ ...p, [skillId]: value }));
  }, []);

  const handleProficiencyClose = useCallback((skillId) => {
    setSelfReportedProficiency((p) => ({ ...p, [skillId]: null }));
    setShowReasonInput((p) => ({ ...p, [skillId]: false }));
  }, []);

  const handleReasonsSubmit = useCallback(
    async (e, skillId, currentProficiency) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        const formData = {
          skill_id: skillId,
          current_proficiency: currentProficiency || "",
          self_reported_proficiency: Number(selfReportedProficiencies[skillId]),
          reasons: reasonText[skillId] || "",
        };
        const response = await submitProficiencyChangeReasons(formData, accessToken);
        if (response?.message) {
          setShowReasonInput((p) => ({ ...p, [skillId]: false }));
          setReasonText((p) => ({ ...p, [skillId]: "" }));
          setViewSuccess(true);
          setTimeout(() => setViewSuccess(false), SUCCESS_TIMEOUT_MS);
        }
      } catch (err) {
        console.error("Failed to submit reason:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [selfReportedProficiencies, reasonText, accessToken]
  );

  const handleSourcesClick = useCallback(() => {
    drawerTypeRef.current = "sources";
    toggleDrawer();
  }, [toggleDrawer]);

  const handleSourcesDrawerClose = useCallback(() => {
    toggleDrawer();
    setViewMode(VIEW_MODE_JD);
  }, [toggleDrawer]);

  const handlePaginationChange = useCallback((page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) setPageSize(size);
  }, [pageSize]);

  const handleViewJdClick = useCallback(() => {
    setJdPanel((prev) => ({ ...prev, visible: !prev.visible }));
  }, []);

  const handleJdClose = useCallback(() => {
    setJdPanel((prev) => ({ ...prev, visible: false }));
  }, []);


  // Advancing off a `handoffToDrawer` step (Source Files) pauses the main tour,
  // opens the Source Files drawer, and starts the in-drawer tour; the main tour
  // resumes at `step` when that finishes (see handleDrawerTourComplete).
  const changeTourStep = useCallback(
    (step) => {
      if (TOUR_STEPS[tourStep]?.handoffToDrawer && step > tourStep) {
        pauseMainTour(step);
        if (!isDrawerOpen) {
          drawerTypeRef.current = "sources";
          toggleDrawer();
        }
        // The drawer waits until it has loaded/settled before opening its tour.
        setDrawerTourActive(true);
        return;
      }
      goToTourStep(step);
    },
    [tourStep, pauseMainTour, goToTourStep, isDrawerOpen, toggleDrawer, drawerTypeRef],
  );

  // Called when the in-drawer tour finishes or is dismissed: close the drawer
  // and resume the main tour at the remembered step.
  const handleDrawerTourComplete = useCallback(() => {
    setDrawerTourActive(false);
    if (isDrawerOpen) toggleDrawer();
    setTimeout(resumeMainTour, 350);
  }, [isDrawerOpen, toggleDrawer, resumeMainTour]);

  // Any exit (X / Cancel / "I'll explore on my own") marks the tour seen so it
  // won't auto-open again; the manual "Take the Tour" button still reopens it.
  const closeTour = useCallback(() => {
    markSeen();
    closeMainTour();
  }, [markSeen, closeMainTour]);

  // Completing the tour also marks it seen, then routes to Source Files upload.
  const finishTour = useCallback(() => {
    markSeen();
    closeMainTour();
    handleSourcesClick();
  }, [markSeen, closeMainTour, handleSourcesClick]);

  return {
    email,
    accessToken,
    isDrawerOpen,
    toggleDrawer,
    drawerTypeRef,
    loading,
    allSkills,
    paginatedSkills,
    sbiscore,
    scaleData,
    jdMarkdown,
    jdPanel,
    showJdPanel,
    viewMode,
    setViewMode,
    currentPage,
    pageSize,
    viewSuccess,
    viewToaster,
    setViewToaster,
    handleViewJdClick,
    handleJdClose,
    isSubmitting,
    selfReportedProficiencies,
    invalidInput,
    activeReasonSkillId,
    activeReasonSkill,
    reasonText,
    handleSelfReportedChange,
    handleReasonTextChange,
    handleProficiencyClose,
    handleReasonsSubmit,
    handleSourcesClick,
    handleSourcesDrawerClose,
    handlePaginationChange,
    tourOpen,
    tourRunId,
    startTour,
    changeTourStep,
    closeTour,
    finishTour,
    tourStep,
    tourTargetRefs,
    drawerTourActive,
    handleDrawerTourComplete,
  };
};

export default useEmployeeSkillsBridger;
